// ✅ קובץ מעודכן המשתמש בתשתית של CustomTreeItem ו־buildTreeItemLabelString
import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { v4 as uuidv4 } from "uuid";
import {
  RichTreeView,
  TreeViewBaseItem,
  TreeViewItemId,
  useTreeViewApiRef,
} from "@mui/x-tree-view";
import {
  formatCurrency,
  formatHandlerCode,
} from "../../../utils/namesFormater";
import {
  Product,
  Mimshak,
  AdjustedProductType,
} from "../../../interfaces/DTO/mislaka_xml_models";
import { MimshakService } from "../../../services/mimshakService";
import {
  castToProductType,
  formatAdjustedProductType,
  formatProductType,
} from "../../../utils/enumFormatter";
import {
  CustomTreeItem,
  buildTreeItemLabelString,
} from "../../../shared/UIParts/CustomTreeItem";
import { colorAdjustedProductType, colorFund, colorProductType } from "../../../utils/colorEnumFormatter";
import InsuranceProductDrawer from "../Insurance/InsuranceProductDrawer";
import HierarchyToggle from "../../../shared/HierarchyToggle";

interface MimshakTreeProps {
  mimshaks: Mimshak[];
  searchResults: string[];
  onSelect?: (selectedItems: string[]) => void;
  storageKey: string;
}

const generateTreeDataProductFirst = (allProducts: Product[]): TreeViewBaseItem[] => {
  const groupedByProductType = allProducts.reduce<Record<string, Product[]>>(
    (acc, product) => {
      const typeKey = String(product.details.adjusted_product_type ?? product.details.product_type);
      if (!acc[typeKey]) acc[typeKey] = [];
      acc[typeKey].push(product);
      return acc;
    },
    {}
  );

  return Object.entries(groupedByProductType).map(([typeKey, products]) => {
    const typeNum = Number(typeKey);
    const formattedProductType = formatAdjustedProductType(typeNum);
    const baseColor = colorAdjustedProductType(typeNum);

    const totalProductTypeAmount = products.reduce(
      (sum, product) =>
        sum + MimshakService.calculateTotals(product.policies_or_accounts),
      0
    );

    const groupedByHandler = products.reduce<Record<string, Product[]>>(
      (acc, product) => {
        const handlerCode = formatHandlerCode(product.details.handler_code);
        if (!acc[handlerCode]) acc[handlerCode] = [];
        acc[handlerCode].push(product);
        return acc;
      },
      {}
    );

    return {
      id: `productType-${uuidv4()}`,
      label: buildTreeItemLabelString({
        primary: formattedProductType,
        secondary: formatCurrency(totalProductTypeAmount),
        colorHex: baseColor,
      }),
      children: Object.entries(groupedByHandler).map(([handlerCode, productsByHandler]) => {
        const totalHandlerAmount = productsByHandler.reduce(
          (sum, product) =>
            sum + MimshakService.calculateTotals(product.policies_or_accounts),
          0
        );

        return {
          id: `handler-${uuidv4()}`,
          label: buildTreeItemLabelString({
            primary: handlerCode,
            secondary: formatCurrency(totalHandlerAmount),
            colorHex: baseColor,
          }),
          children: productsByHandler.flatMap((product) =>
            product.policies_or_accounts.map((policy) => {
              const policyAmount = MimshakService.calculatePolicyTotals(policy);

              return {
                id: `policy--${uuidv4()}||${policy.policy_number}`,
                label: buildTreeItemLabelString({
                  primary: `פוליסה: ${policy.policy_number}`,
                  secondary: formatCurrency(policyAmount),
                  colorHex: baseColor,
                }),
                children: MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths).map((path) => {
                  return {
                    id: `path-${uuidv4()}`,
                    label: buildTreeItemLabelString({
                      primary: path.path_name,
                      secondary: formatCurrency(path.accumulated_amount || 0),
                      colorHex: colorFund(path.path_code, baseColor),
                    }),
                  };
                })
              };
            })
          ),
        };
      }),
    };
  });
};

const getEffectiveType = (product: Product): AdjustedProductType =>
  (product?.details?.adjusted_product_type ?? product?.details?.product_type) as AdjustedProductType;

const generateTreeDataHandlerFirst = (allProducts: Product[]): TreeViewBaseItem[] => {
  // קיבוץ ראשון לפי handler
  const groupedByHandler = allProducts.reduce<Record<string, Product[]>>((acc, product) => {
    const rawHandlerCode = String(product.details.handler_code);
    (acc[rawHandlerCode] ||= []).push(product);
    return acc;
  }, {});

  return Object.entries(groupedByHandler).map(([rawHandlerCode, products]) => {
    const handlerCode = formatHandlerCode(rawHandlerCode);
    const totalHandlerAmount = products.reduce(
      (sum, p) => sum + MimshakService.calculateTotals(p.policies_or_accounts),
      0
    );

    // קיבוץ שני לפי adjusted_product_type (Fallback ל־product_type)
    const groupedByType = products.reduce<Record<string, Product[]>>((acc, p) => {
      const eff = getEffectiveType(p);
      const key = String(eff);
      (acc[key] ||= []).push(p);
      return acc;
    }, {});

    return {
      id: `handler-${uuidv4()}`,
      label: buildTreeItemLabelString({
        primary: handlerCode,
        secondary: formatCurrency(totalHandlerAmount),
      }),
      children: Object.entries(groupedByType).map(([typeKey, productsByType]) => {
        const typeNum = Number(typeKey) as AdjustedProductType;
        const typeLabel = formatAdjustedProductType(typeNum);
        const typeColor = colorAdjustedProductType(typeNum);

        const totalTypeAmount = productsByType.reduce(
      (sum, p) => sum + MimshakService.calculateTotals(p.policies_or_accounts),
          0
        );

        return {
          id: `productType-${uuidv4()}`,
          label: buildTreeItemLabelString({
            primary: typeLabel,
            secondary: formatCurrency(totalTypeAmount),
            colorHex: typeColor,
          }),
          children: productsByType.flatMap((product) =>
            product.policies_or_accounts.map((policy) => {
              const policyAmount = MimshakService.calculatePolicyTotals(policy);
              return {
                id: `policy--${uuidv4()}||${policy.policy_number}`,
                label: buildTreeItemLabelString({
                  primary: `פוליסה: ${policy.policy_number}`,
                  secondary: formatCurrency(policyAmount),
                  colorHex: typeColor,
                }),
                children: MimshakService
                  .mergeDuplicateInvestmentPaths(policy.investment_paths)
                  .map((path) => ({
                    id: `path-${uuidv4()}`,
                    label: buildTreeItemLabelString({
                      primary: path.path_name,
                      secondary: formatCurrency(path.accumulated_amount || 0),
                      colorHex: colorFund(path.path_code, typeColor),
                    }),
                  })),
              };
            })
          ),
        };
      }),
    };
  });
};

export const MimshakTree: React.FC<MimshakTreeProps> = ({ mimshaks, searchResults, onSelect,storageKey }) => {
  const [hierarchyOrder, setHierarchyOrder] = useState<'handlerFirst' | 'productFirst'>("handlerFirst");

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const toggledItemRef = React.useRef<{ [itemId: string]: boolean }>({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const apiRef = useTreeViewApiRef();
  const allProducts = useMemo(() => MimshakService.getAllProducts(mimshaks), [mimshaks]);

  // ✅ ספירת כל הפוליסות (עלים בעץ)
  const totalPoliciesCount = useMemo(() => {
    return allProducts.reduce((count, product) =>
      count + (product.policies_or_accounts?.length || 0), 0
    );
  }, [allProducts]);

  useEffect(() => {
    handleSelectedItemsChange(null as any, []);
  }, [hierarchyOrder]);

  const handleCloseDrawer = () => setSelectedProduct(null);

  const treeData: TreeViewBaseItem[] = useMemo(() => {
    return hierarchyOrder === 'handlerFirst'
      ? generateTreeDataHandlerFirst(allProducts)
      : generateTreeDataProductFirst(allProducts);
  }, [allProducts, hierarchyOrder]);

  const getItemDescendantsIds = (item: TreeViewBaseItem): string[] => {
    const ids: string[] = [];
    item.children?.forEach((child) => {
      ids.push(child.id);
      const fullChild = (apiRef.current as any)?.getItem(child.id);
      if (fullChild) ids.push(...getItemDescendantsIds(fullChild));
    });
    return ids;
  };

  const handleItemSelectionToggle = (
    _event: React.SyntheticEvent,
    itemId: string,
    isSelected: boolean
  ) => {
    const item = (apiRef.current as any)?.getItem(itemId);
    if (item) toggledItemRef.current[itemId] = isSelected;
  };

  const handleSelectedItemsChange = (
    _event: React.SyntheticEvent,
    newSelectedItems: string[]
  ) => {
    setSelectedItems(newSelectedItems);

    const itemsToSelect: string[] = [];
    const itemsToUnSelect: { [itemId: string]: boolean } = {};

    Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]) => {
      const item = (apiRef.current as any)?.getItem(itemId);
      if (item) {
        if (isSelected) {
          itemsToSelect.push(...getItemDescendantsIds(item));
        } else {
          getItemDescendantsIds(item).forEach((descendantId) => {
            itemsToUnSelect[descendantId] = true;
          });
        }
      }
    });

    const newSelectedItemsWithChildren = Array.from(
      new Set([...newSelectedItems, ...itemsToSelect].filter((id) => !itemsToUnSelect[id]))
    );

    setSelectedItems(newSelectedItemsWithChildren);

    const selectedPolicies = newSelectedItemsWithChildren
      .filter((itemId) => itemId.startsWith("policy--") && itemId.includes("||"))
      .map((itemId) => itemId.split("||")[1]); // מחלץ רק את policy_number

    if (onSelect) onSelect(selectedPolicies);
    toggledItemRef.current = {};
  };

  const handleClearAll = () => {
    handleSelectedItemsChange(null as any, []);
  };

  return (
    <Box sx={{ p: 2, maxHeight: "100%", overflow: "auto" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">
            {selectedItems.filter(id => id.startsWith("policy--")).length > 0
              ? `עץ התיק (${selectedItems.filter(id => id.startsWith("policy--")).length}/${totalPoliciesCount})`
              : `עץ התיק (${totalPoliciesCount})`}
          </Typography>
          {selectedItems.length > 0 && (
            <IconButton
              size="small"
              onClick={handleClearAll}
              title="נקה הכל"
              sx={{
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.dark" },
                width: 28,
                height: 28,
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <HierarchyToggle

          onChange={setHierarchyOrder}
          storageKey={storageKey}
        />
      </Box>

      <RichTreeView
        items={treeData}
        getItemId={(item) => item.id}
        getItemLabel={(item) => item.label}
        selectedItems={selectedItems}
        onSelectedItemsChange={handleSelectedItemsChange}
        onItemSelectionToggle={handleItemSelectionToggle}
        checkboxSelection
        multiSelect
        apiRef={apiRef}
        slots={{ item: (props: any) => <CustomTreeItem
           {...props}
           products={allProducts}
           setSelectedProduct={setSelectedProduct}
          /> }}
      />
      {selectedProduct && (
        <InsuranceProductDrawer
          product={selectedProduct}
          onClose={handleCloseDrawer}
        />
      )}
    </Box>
  );
};
